import React, { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Search as SearchIcon } from "@material-ui/icons";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllPartners } from "src/apps/commons/dropdowns/api";
import { IPartnerBrief } from "../../partner/types";

const useStyles = makeStyles(theme => ({
  searchContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  searchInput: {
    marginTop: 0,
    marginBottom: theme.spacing(1)
  }
}));

interface Props {
  onChangeSearchTerm: (searchTerm: string) => void;
  onChangeVerified: (verified?: boolean) => void;
  onChangePartnerVerified: (verified?: boolean) => void;
  onChangeUserDeleted: (deleted?: boolean) => void;
  onChangeRequestWarranty: (request?: boolean) => void;
  onChangePartnerId: (partnerId?: PartnerId) => void;
}

type BooleanRadioValue = "" | "true" | "false";
const parseBooleanRadio = (input: BooleanRadioValue) =>
  input === "true" ? true : input === "false" ? false : undefined;

const BooleanRadioGroup = (props: {
  label: string;
  value: BooleanRadioValue;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  trueLabel?: string;
  falseLabel?: string;
}) => {
  const { label, value, onChange, trueLabel, falseLabel } = props;
  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">{label}</FormLabel>
      <RadioGroup
        row
        aria-label="position"
        name={label}
        value={value}
        onChange={onChange}
      >
        <FormControlLabel
          value=""
          control={<Radio color="primary" />}
          label="ทั้งหมด"
        />
        <FormControlLabel
          value="true"
          control={<Radio color="primary" />}
          label={trueLabel ?? "Y"}
        />
        <FormControlLabel
          value="false"
          control={<Radio color="primary" />}
          label={falseLabel ?? "N"}
        />
      </RadioGroup>
    </FormControl>
  );
};

const ListSearchPanel: React.FC<Props> = props => {
  const {
    onChangeSearchTerm,
    onChangeVerified,
    onChangePartnerVerified,
    onChangeUserDeleted,
    onChangeRequestWarranty,
    onChangePartnerId
  } = props;
  const classes = useStyles();

  const [verified, setVerified] = useState<BooleanRadioValue>("");
  const [partnerVerified, setPartnerVerified] = useState<BooleanRadioValue>("");
  const [userDeleted, setUserDeleted] = useState<BooleanRadioValue>("false");
  const [requestWarranty, setRequestWarranty] = useState<BooleanRadioValue>("");

  const handleChangeSearchTerm = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const searchTerm = e.target.value ?? "";
    onChangeSearchTerm(searchTerm);
  };

  const handleChangeVerified = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value as BooleanRadioValue;
    setVerified(input);
    onChangeVerified(parseBooleanRadio(input));
  };
  const handleChangePartnerVerified = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.target.value as BooleanRadioValue;
    setPartnerVerified(input);
    onChangePartnerVerified(parseBooleanRadio(input));
  };
  const handleChangeUserDeleted = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value as BooleanRadioValue;
    setUserDeleted(input);
    onChangeUserDeleted(parseBooleanRadio(input));
  };
  const handleChangeRequestWarranty = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.target.value as BooleanRadioValue;
    setRequestWarranty(input);
    onChangeRequestWarranty(parseBooleanRadio(input));
  };

  const [partner, setPartner] = useState<IDropdown<IPartnerBrief> | null>(null);

  return (
    <Paper className={classes.searchContainer}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={8}>
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            placeholder="ค้นหาจาก ชื่อ, แบรนด์, หมวดหมู่, รายละเอียด User (ชื่อ, Email, เบอร์โทร), ร้านค้าที่ซื้อ, เลขที่ใบเสร็จ"
            name="searchTerm"
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            onChange={handleChangeSearchTerm}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <AsyncDropdown
            label={"Partner"}
            fetchDropdown={listAllPartners}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME",
                desc: false
              }
            }}
            value={partner}
            onChange={value => {
              setPartner(value);
              onChangePartnerId(value?.value ?? undefined);
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Box ml={1}>
            <BooleanRadioGroup
              label="Remynd Verified"
              value={verified}
              onChange={handleChangeVerified}
              trueLabel="Verified"
              falseLabel="Not Verified"
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Box ml={1}>
            <BooleanRadioGroup
              label="Partner Verified"
              value={partnerVerified}
              onChange={handleChangePartnerVerified}
              trueLabel="Verified"
              falseLabel="Not Verified"
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Box ml={1}>
            <BooleanRadioGroup
              label="User ต้องการให้ช่วยลงทะเบียนการรับประกัน"
              value={requestWarranty}
              onChange={handleChangeRequestWarranty}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Box ml={1}>
            <BooleanRadioGroup
              label="User ลบข้อมูล"
              value={userDeleted}
              onChange={handleChangeUserDeleted}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ListSearchPanel;
